import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { References } from '@/classes/scenario/pv/SurfaceDefinitionExt';

interface ReferencesService {
  getInflationRef: () => Promise<Record<string, unknown>>;
  getFxRef: () => Promise<Record<string, unknown>>;
  getFdRef: () => Promise<Record<string, unknown>>;
}

type Dependencies = {
  loadFile: <FileContent>(fileName: string) => Promise<{ data: FileContent }>;
  errorHandler: (error: unknown) => void;
  referencesService: ReferencesService;
}

export const buildReferencesModule = ({ loadFile, errorHandler, referencesService }: Dependencies) => {
  @Module({ namespaced: true })
  class ReferencesModule extends VuexModule {
    private references?: References;
    private inflation: Record<string, unknown> | null = null;
    private fx: Record<string, unknown> | null = null;
    private fd: Record<string, unknown> | null = null;

    @Action({ rawError: true })
    async init() {
      const { data: references } = await loadFile<References>('surface_definition');
      this.context.commit('setSurfaceDefinition', references);
    }

    @Mutation
    setSurfaceDefinition(references: References) {
      this.references = references;
    }

    get surfaceDefinition(): References {
      if (this.references === undefined) {
        throw new Error('Reference module has not been initiated yet');
      }
      return this.references;
    }

    @Action
    async loadInflation(): Promise<typeof ReferencesModule.prototype.inflation> {
      if (this.inflation !== null) {
        return this.inflation;
      }

      try {
        const inflation = await referencesService.getInflationRef();
        if (inflation) {
          this.context.commit('setInflation', inflation);
        }
      } catch (error) {
        errorHandler(error);
      }
      return this.inflation;
    }

    @Mutation
    private setInflation(data: any) {
      this.inflation = data;
    }

    get getInflation() {
      return this.inflation;
    }

    @Action
    async loadFx(): Promise<typeof ReferencesModule.prototype.fx> {
      if (this.fx !== null) {
        return this.fx;
      }

      try {
        const fx = await referencesService.getFxRef();
        if (fx) {
          this.context.commit('setFx', fx);
        }
      } catch (error) {
        errorHandler(error);
      }
      return this.fx;
    }

    @Mutation
    private setFx(data: any) {
      this.fx = data;
    }

    get getFx() {
      return this.fx;
    }

    @Action
    async loadFd(): Promise<typeof ReferencesModule.prototype.fd> {
      if (this.fd !== null) {
        return this.fd;
      }

      try {
        const fd = await referencesService.getFdRef();
        if (fd) {
          this.context.commit('setFd', fd);
        }
      } catch (error) {
        errorHandler(error);
      }
      return this.fd;
    }

    @Mutation
    private setFd(data: any) {
      this.fd = data;
    }

    get getFd() {
      return this.fd;
    }
  }

  return ReferencesModule;
};
